import { cubicEaseOut, shouldAnimate, transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { SitePadding, SitePaddingMobile, siteWide, SiteWidth } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { paragraphRegular } from '@helpers/typography';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import BreadcrumbStyles from '@stories/Components/General/Breadcrumb/Breadcrumb.styles';
import UmbracoFormStyles from '@stories/Components/UmbracoForm/UmbracoForm.styles';
import { rgba } from 'polished';
import styled, { createGlobalStyle, css } from 'styled-components';
const GlobalStyles = createGlobalStyle `
  :root {
    --headerHeight: ${fluid(113, 153, Device.Tablet, Device.TabletLarge)};
    --stickyHeaderHeight: var(--headerHeight);

    @media ${from(Device.TabletLarge)} {
      --headerHeight: ${fluid(190, 199, Device.TabletLarge)};
      --stickyHeaderHeight: 105px;
    }
  }
`;
const BackgroundOverlay = styled.button `
  ${ButtonReset};
  ${transition('opacity', 0.5)};

  position: fixed;
  z-index: -1;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: ${rgba(brand.tint.purple, 0.3)};
  cursor: zoom-out;

  &[data-active='false'] {
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const Container = styled.header `
  ${siteWide};

  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${Layers.Header};
  background: transparent;

  ${BreadcrumbStyles.Container} {
    ${transition('max-height')};
    overflow: hidden;
    max-height: 60px;
    position: relative;
    z-index: 0;
  }

  &[data-sticky='true'] {
    ${BreadcrumbStyles.Container} {
      max-height: 0;
    }
  }

  &[data-sticky='false'] {
    position: absolute;
  }

  .equally-ai___high-saturation & {
    filter: saturate(200%);
  }

  .equally-ai___reverse-contrast & {
    filter: invert(100%);
  }

  .equally-ai___monochrome-contrast & {
    filter: grayscale(100%);
  }

.equally-ai___reverse-contrast.equally-ai___monochrome-contrast & {
  filter: grayscale(100%) invert(100%);
}
`;
const TopLinksWrapper = styled.section `
  margin-left: auto;
  display: inline-flex;
  background-color: ${brand.white};

  // ensure z-index so overlapping link hover state displays correctly
  position: relative;
  z-index: 2;

  @media ${from(Device.TabletLarge)} {
    ${transition('opacity', 0.66)};

    max-height: 35px;

    ${Container}[data-sticky='true'] & {
      opacity: 0;
      pointer-events: none;
    }
  }
`;
const QuickLinks = styled.div `
  display: none;

  @media ${from(Device.TabletLarge)} {
    display: inline-flex;
    background-color: ${brand.secondary.red};
    gap: 20px;
    padding: 0 20px;
  }
`;
const topLinkStyles = css `
  ${fonts.graphikCompact.medium};

  height: ${fluid(30, 35)};
  display: inline-flex;
  align-items: center;
  font-size: 18px;
  line-height: 24px;
  color: ${brand.primary.purple};

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }
`;
const QuickLink = styled.a `
  ${transition('color')};
  ${topLinkStyles};

  position: relative;
  text-decoration: none;
  padding: 4px 0 6px;

  &::after {
    ${transition('background-size')};

    content: '';
    display: block;
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    width: 100%;
    background: transparent;
    background-image: linear-gradient(
      to right,
      ${brand.primary.offWhite},
      ${brand.primary.offWhite}
    );
    background-repeat: no-repeat;
    background-size: 0% 100%;
    transform: translateY(100%);
  }

  &.focus-ring,
  &:hover {
    color: ${brand.primary.offWhite};

    &::after {
      background-size: 100% 100%;
    }
  }
`;
const AccessibilityBtn = styled.button `
  ${transition('background-color, color')};
  ${ButtonReset};
  ${topLinkStyles};

  background-color: ${brand.primary.offWhite};
  padding: 0 ${fluid(10, 20)};
  gap: 8px;
  border-bottom: 3px solid transparent;
  position: relative;

  > span {
    padding: 5px 0;
  }

  > svg {
    display: block;
    height: ${fluid(18, 22)};
    width: ${fluid(18, 22)};
  }

  &::after {
    ${transition('background-size')};

    content: '';
    display: block;
    position: absolute;
    bottom: -6px;
    left: 0;
    height: 3px;
    width: 100%;
    background: transparent;
    background-image: linear-gradient(
      to right,
      ${brand.primary.offWhite},
      ${brand.primary.offWhite}
    );
    background-repeat: no-repeat;
    background-size: 0% 100%;
  }

  &.focus-ring,
  &:hover {
    background-color: ${brand.primary.purple};
    color: ${brand.primary.offWhite};

    &::after {
      background-size: 100% 100%;
    }
  }

  @media ${until(Device.TabletLarge)} {
    > span {
      ${srOnly};
    }
  }
`;
const MainWrapper = styled.section `
  position: relative;
  z-index: 1;
  background-color: ${brand.primary.purple};
  padding: ${fluid(18, 30)} ${fluid(20, 30)};
  display: grid;
  grid-template-columns: ${fluid(135, 270)} auto;
  gap: 0 16px;

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: repeat(36, 1fr);
    padding-bottom: 0;
    gap: 26px 12px;

    ${Container}[data-sticky='true'] & {
      padding-top: 28px;
      padding-top: 10px;
    }
  }

  /* sticky nav glow */
  &::after {
    ${transition('opacity', 0.66)};

    position: absolute;
    z-index: -1;
    pointer-events: none;
    content: '';
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-shadow: 0 0 10px 5px rgba(255, 255, 255, 0.33);
    opacity: 0;

    ${Container}[data-sticky='true'] & {
      opacity: 1;
    }
  }
`;
const Logo = styled.a `
  color: ${brand.white};

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }

  > svg {
    ${transition('max-width', 0.66)};

    display: block;
    max-width: ${fluid(135, 270)};
    width: 100%;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 7;
    grid-row: span 2;

    ${Container}[data-sticky='true'] & {
      grid-column: 1 / span 4;
      grid-row: span 1;

      > svg {
        max-width: 135px;
      }
    }
  }
`;
const MobileButtons = styled.div `
  display: flex;
  align-items: center;
  gap: 16px;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const MenuButton = styled.button `
  ${ButtonReset};
  ${fonts.graphikCompact.regular};
  ${transition('opacity')};

  display: inline-flex;
  align-items: flex-end;
  gap: 6px;
  color: ${brand.primary.offWhite};
  font-size: 16px;
  line-height: 20px;
  padding: 4px 0;
  margin: 0 0 0 auto;

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }

  &.focus-ring,
  &:hover {
    opacity: 0.66;
  }
`;
const MenuIcon = styled.span `
  display: block;
  position: relative;
  width: 24px;
  height: 16px;

  > span {
    ${transition('transform, opacity')};

    display: block;
    position: absolute;
    height: 2px;
    width: 50%;
    background-color: currentColor;

    &:nth-child(even) {
      left: 50%;
      border-radius: 0 2px 2px 0;
    }

    &:nth-child(odd) {
      left: 0px;
      border-radius: 2px 0 0 2px;
    }

    &:nth-child(1),
    &:nth-child(2) {
      top: 0;
    }

    &:nth-child(3),
    &:nth-child(4) {
      top: 7px;
    }

    &:nth-child(5),
    &:nth-child(6) {
      top: 14px;
    }
  }

  ${MenuButton}[data-active='true'] & {
    > span {
      &:nth-child(3),
      &:nth-child(4) {
        opacity: 0;
      }

      &:nth-child(3) {
        transform: translateX(-100%);
      }

      &:nth-child(4) {
        transform: translateX(100%);
      }

      &:nth-child(1) {
        transform: translate(2px, 150%) rotate(45deg);
      }

      &:nth-child(2) {
        transform: translate(-2px, 150%) rotate(-45deg);
      }

      &:nth-child(5) {
        transform: translate(2px, -150%) rotate(-45deg);
      }

      &:nth-child(6) {
        transform: translate(-2px, -150%) rotate(45deg);
      }
    }
  }
`;
const SearchCtaWrapper = styled.div `
  display: flex;

  @media ${until(Device.TabletLarge)} {
    ${transition('max-height', 0.66)};

    max-height: 0;
    grid-column: span 2;
    overflow: hidden;

    &[data-open='true'] {
      max-height: ${fluid(62, 64)};
    }

    &[data-show-suggestions='true'] {
      overflow: visible;
    }

    ${ButtonStyles.Button} {
      display: none;
    }
  }

  @media ${from(Device.TabletLarge)} {
    justify-content: flex-end;
    grid-column: 14 / span 23;
    gap: 12px;

    ${ButtonStyles.Button} {
      flex-shrink: 0;
    }

    ${Container}[data-sticky='true'] & {
      grid-row: 1 / span 1;

      ${ButtonStyles.Button}:nth-of-type(2) {
        display: none;
      }
    }
  }
`;
const SearchForm = styled.form `
  position: relative;
  height: ${fluid(46, 48)};
  width: 100%;
  margin: 16px 0 0;
  border: 1px solid ${brand.primary.offWhite};
  border-radius: 5000px;

  @media ${from(Device.TabletLarge)} {
    ${transition('max-width', 0.66)};

    max-width: 525px;
    margin: 0;

    ${Container}[data-sticky='true'] & {
      max-width: ${fluid(46, 48)};
    }

    ${SearchCtaWrapper}[data-open='true'] & {
      max-width: 825px;

      /* Ensure search sits above navigation links */
      z-index: 1;
    }
  }
`;
const SearchInput = styled.input `
  ${fonts.graphikCompact.regular};

  height: 100%;
  width: 100%;
  border: 0;
  border-radius: 5000px;
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(20, 24)};
  color: ${brand.primary.offWhite};
  background-color: transparent;
  margin: 0;
  padding: 10px 52px 13px 20px;

  &::placeholder {
    opacity: 0.66;
  }

  ${Container}[data-sticky='true'] & {
    display: none;
  }

  ${SearchCtaWrapper}[data-open='true'] & {
    display: block;
  }
`;
const SearchButton = styled.button `
  ${ButtonReset};
  ${transition('opacity, transform')};

  display: grid;
  place-content: center;
  height: 40px;
  width: 40px;
  color: ${brand.primary.offWhite};
  border-radius: 5000px;

  > svg {
    ${transition('opacity')};

    display: block;
    height: ${fluid(16, 22)};
    width: ${fluid(16, 22)};
    grid-area: 1 / 1;
  }

  > svg:nth-child(2) {
    color: ${brand.secondary.red};
  }

  &[data-active='true'] {
    > svg:nth-child(1) {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }
  }

  &[data-active='false'] {
    > svg:nth-child(2) {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }

  &.focus-ring,
  &:hover {
    opacity: 0.66;
  }

  ${SearchForm} & {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);

    &:nth-of-type(1) {
      right: 8px;

      ${Container}[data-sticky='true'] & {
        opacity: 0;
      }

      ${SearchCtaWrapper}[data-open='true'] & {
        opacity: 1;
        z-index: 2;
      }
    }

    &:nth-of-type(2) {
      opacity: 0;
      left: 3px;
      visibility: hidden;
      pointer-events: none;

      &[data-active='true'] {
        transform: translate(-133%, -50%);
      }

      ${Container}[data-sticky='true'] & {
        opacity: 1;
        z-index: 2;
        visibility: visible;
        pointer-events: all;

        @media ${until(Device.TabletLarge)} {
          opacity: 0;
        }
      }
    }
  }

  ${MobileButtons} & {
    border: 2px solid ${brand.primary.offWhite};
  }
`;
const SearchError = styled(UmbracoFormStyles.Error) `
  ${fonts.graphikCompact.medium};

  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -9px);
  font-size: ${fluid(13, 14)};
  line-height: 22px;
  letter-spacing: 0.075em;
  color: ${brand.secondary.red};
  background: ${brand.primary.purple};
  padding: 0 4px;
  white-space: nowrap;

  @media ${from(Device.TabletLarge)} {
    ${transition('opacity')};

    left: auto;
    right: 42px;
    transform: translateY(-9px);
  }

  /* Fade in error message when populated */
  ${shouldAnimate(css `
    opacity: 0;
    animation: 350ms ${cubicEaseOut} forwards fadeIn;
    animation-delay: 50ms;

    /* Ennsure is it hidden when sticky search is closed */
    @media ${from(Device.TabletLarge)} {
      ${Container}[data-sticky='true'] & {
        animation-fill-mode: backwards;
      }

      ${SearchCtaWrapper}[data-open='true'] & {
        animation-fill-mode: forwards;
      }
    }
  `)}

  @keyframes fadeIn {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`;
const Autosuggest = styled.ul `
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 2;
  width: 100%;
`;
const AutosuggestOption = styled.button `
  ${ButtonReset};
  ${transition('color')};
  ${paragraphRegular};

  display: block;
  width: calc(100% - 36px);
  color: ${brand.primary.purple};
  background-color: ${brand.primary.offWhite};
  text-align: left;
  padding: 5px 7px 7px;
  margin: 0 auto;
  border-bottom: 1px solid ${rgba(brand.tint.purple, 0.2)};

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }

  &.focus-ring,
  &:hover {
    color: ${brand.secondary.red};
  }

  li:last-of-type & {
    border: 0;
  }
`;
const AutosuggestLink = styled.a `
  ${transition('color')};
  ${paragraphRegular};

  display: block;
  width: calc(100% - 36px);
  color: ${brand.primary.purple};
  background-color: ${brand.primary.offWhite};
  text-align: left;
  padding: 5px 7px 7px;
  margin: 0 auto;
  border-bottom: 1px solid ${rgba(brand.tint.purple, 0.2)};

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }

  &.focus-ring,
  &:hover {
    color: ${brand.secondary.red};
  }

  li:last-of-type & {
    border: 0;
  }
`;
const DesktopNavigation = styled.div `
  display: none;

  @media ${from(Device.TabletLarge)} {
    ${transition('opacity')};

    display: inline-flex;
    grid-column: 8 / span 29;
    height: 60px;

    ${Container}[data-sticky='true'] & {
      grid-column: 5 / span 26;
      grid-row: 1 / span 1;

      &[data-search-open='true'] {
        opacity: 0;
      }
    }

    ${Container}[data-sticky='true'][data-logged-in='true'] & {
      grid-column: 5 / span 25;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    ${Container}[data-sticky='true'] & {
      grid-column: 5 / span 27;
    }

    ${Container}[data-sticky='true'][data-logged-in='true'] & {
      grid-column: 5 / span 26;
    }
  }
`;
const StickyCta = styled.div `
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: ${fluid(12, 14)} ${fluid(18, 24)} ${fluid(12, 14)} ${fluid(75, 75)};
  background-color: ${brand.primary.purple};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  z-index: ${Layers.Header};

  ${ButtonStyles.Button} {
    flex-shrink: 0;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const StickyCtaText = styled.p `
  ${paragraphRegular};

  color: ${brand.primary.offWhite};
  margin-bottom: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
`;
const BackToTop = styled.button `
  --sitePadding: ${fluid(SitePaddingMobile, SitePadding)};
  --siteWidth: ${SiteWidth}px;

  ${ButtonReset};
  ${transition('opacity, transform, grid-template-rows', 0.6)};
  ${fonts.graphikCompact.regular};

  color: ${brand.primary.offWhite};
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  position: fixed;
  right: var(--sitePadding);
  bottom: 0;
  opacity: 0;
  transform: translateX(100%);
  z-index: ${Layers.Header};
  background-color: ${brand.primary.purple};
  padding: 0 6px;

  /* 
  Force text and icon to overlap and 
  position with translate to prevent 
  max-height animation moving the text
  */
  display: grid;
  align-items: center;
  grid-template-rows: 26px 24px 6px;

  > span {
    grid-area: 2 / 1;
    transform: translateY(100%);

    @media ${until(Device.TabletLarge)} {
      ${srOnly};
    }
  }

  &[data-visible='true'] {
    opacity: 1;
    transform: none;
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }

  /* Add extra space beneath to compensate for mobile sticky CTA banner */
  @media ${until(Device.TabletLarge)} {
    &[data-has-cta='true'] {
      bottom: ${fluid(92, 96)};
    }
  }

  @media ${from(Device.TabletLarge)} {
    grid-template-rows: 26px 54px 9px;
    padding-left: 10px;
    padding-right: 10px;

    &.focus-ring,
    &:hover {
      grid-template-rows: 32px 54px 9px;
    }
  }

  @media ${from(Device.TabletLarge)} {
    &[data-mobile-only='true'] {
      display: none;
    }
  }

  @media ${from(Device.ActualDesktop)} {
    right: calc((100% - var(--siteWidth)) / 2);
  }
`;
const BackToTopIcon = styled.div `
  ${transition('max-height')};

  grid-area: 2 / 1;
  margin: 0 auto;
  display: grid;
  place-content: center;
  border: 2px solid currentColor;
  border-radius: 5000px;
  width: 46px;
  max-height: 46px;
  height: 100%;
  transform-origin: bottom;
  transform: translateY(-40%);

  > svg {
    ${transition('transform')};

    grid-area: 1 / 1;
    display: block;
    width: 9px;
    transform: rotate(-90deg) translateX(-25%);
  }

  ${BackToTop}.focus-ring &,
  ${BackToTop}:hover & {
    max-height: 54px;

    > svg {
      transform: rotate(-90deg) translateX(6px);
    }
  }

  @media ${until(Device.TabletLarge)} {
    border: 0;
    max-height: 54px;

    > svg {
      transform: rotate(-90deg) translateX(6px);
    }
  }
`;
const HoverLine = styled.div `
  ${transition('transform')};

  grid-area: 1 / 1;
  margin: 0 auto;
  width: 3px;
  height: 18px;
  transform: scaleY(0);
  background-color: ${brand.primary.offWhite};

  ${BackToTop}.focus-ring &,
  ${BackToTop}:hover & {
    transform: scaleY(1);
  }

  @media ${until(Device.TabletLarge)} {
    transform: scaleY(1);
  }
`;
const HeaderStyles = {
    AccessibilityBtn,
    Autosuggest,
    AutosuggestLink,
    AutosuggestOption,
    Container,
    DesktopNavigation,
    GlobalStyles,
    Logo,
    MainWrapper,
    MenuButton,
    MenuIcon,
    MobileButtons,
    QuickLink,
    QuickLinks,
    SearchButton,
    SearchCtaWrapper,
    SearchError,
    SearchForm,
    SearchInput,
    TopLinksWrapper,
    StickyCta,
    StickyCtaText,
    BackToTop,
    BackToTopIcon,
    HoverLine,
    BackgroundOverlay,
};
export default HeaderStyles;
